// One visual language for every dropdown on the site: the navbar language switcher
// and the template filter menus. These live in one place so the two cannot drift
// apart — layout that is genuinely per-instance (width, left/right anchoring,
// min-width) stays with the component and is passed in as `extra`.
//
// NOTE: text colour and font-size are deliberately NOT set here. The ported Webflow
// sheets are UNLAYERED and therefore outrank Tailwind, which v4 emits inside
// `@layer utilities` — `normalize.css` sets `button { font: inherit; color: inherit }`
// and `wedoflow.webflow.css` sets `a { color: red }`. Those two properties are owned
// by the `.dropdown-trigger` / `.dropdown-row` rules in `custom.css`, which is the
// last unlayered stylesheet and so actually wins. The class hooks below are what
// those rules attach to; don't remove them.
import { cn } from '@/lib/utils'

/** Pill/button that opens a menu. `extra` carries per-instance sizing. */
export function dropdownTriggerClass(open: boolean, extra?: string) {
    return cn(
        'dropdown-trigger',
        'flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-2.5 font-medium',
        'transition-colors duration-200 outline-none',
        'border-white/10 bg-[#181818]',
        'hover:border-white/20 hover:bg-[#222]',
        'focus-visible:ring-2 focus-visible:ring-white/25',
        open && 'border-white/25 bg-[#222]',
        extra
    )
}

/** Chevron affordance on a trigger. */
export function dropdownChevronClass(open: boolean) {
    return cn(
        'size-4 shrink-0 text-white/50 transition-transform duration-200',
        open && 'rotate-180 text-white/80'
    )
}

/** Check mark marking the current selection. */
export const DROPDOWN_CHECK_CLASS = 'size-4 shrink-0 text-white/70'

/** Surface of the open menu. Position and width are supplied by the caller. */
export const DROPDOWN_MENU_CLASS = cn(
    'overflow-hidden rounded-xl border border-white/10 bg-[#161616] shadow-2xl shadow-black/60',
    'animate-in fade-in-0 zoom-in-95 slide-in-from-top-1 duration-150'
)

/** A single selectable row. Colour comes from `custom.css` (see note above). */
export function dropdownRowClass(active: boolean) {
    return cn(
        'dropdown-row',
        'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left',
        'hover:bg-white/5',
        active && 'is-active bg-white/5'
    )
}
